import { UserAvatar } from '@/components/UserAvatar';
import { cn } from '@/lib/utils';
import { authProfileState, userState } from '@/state';
import { useLoadableAtomValue } from '@/state/loadableHooks';
import type { AuthProfile, UserId } from '@rock/types';
import { Loader2 } from 'lucide-react';
import type { FC } from 'react';
import { Link } from 'react-router-dom';

type UserChipSize = 'sm' | 'md' | 'lg';

type UserChipProps = {
    userId: UserId | undefined;
    size?: UserChipSize;
    /** Adds the email under the name, when it isn't already the name we're showing. */
    showEmail?: boolean;
    layout?: 'inline' | 'stacked';
    align?: 'start' | 'end';
    /** Links through to the user's page. Leave off inside a row or card that already navigates. */
    link?: boolean;
    className?: string;
};

const SIZES: Record<UserChipSize, { avatar: string; name: string }> = {
    // The fallback sets its own text size, so the small chip's initials target it directly.
    sm: { avatar: 'size-5 *:data-[slot=avatar-fallback]:text-[10px]', name: 'text-xs font-medium' },
    md: { avatar: 'size-8', name: 'text-sm font-medium' },
    lg: { avatar: 'size-10', name: 'text-sm font-medium' },
};

/**
 * Who someone is — their photo (initials when they have none) and their name, resolved from a user
 * id. Shared so a person reads the same everywhere: an owner on a home, an approver on a byline, a
 * name in a table cell. Falls back to the email, then the raw id, so it always says something. A uid
 * with no user doc is looked up in Firebase Auth (admins have no `users/{id}`).
 */
export const UserChip: FC<UserChipProps> = (props) => {
    const { userId } = props;
    const [user, userLoadable] = useLoadableAtomValue(userState(userId));

    if (!userId) return null;
    if (userLoadable.state === 'loading') return <ChipLoading {...props} />;
    if (user) return <ChipView {...props} userId={userId} profile={user} />;
    return <AuthFallbackChip {...props} userId={userId} />;
};

const AuthFallbackChip: FC<UserChipProps & { userId: UserId }> = (props) => {
    const [profile, loadable] = useLoadableAtomValue(authProfileState(props.userId));
    if (loadable.state === 'loading') return <ChipLoading {...props} />;
    // No user doc means no user page to link to — it would only say "User not found."
    return <ChipView {...props} profile={profile} link={false} />;
};

const ChipLoading: FC<UserChipProps> = ({ size = 'md', className }) => (
    <div className={cn('flex items-center gap-2 text-xs text-muted-foreground', className)}>
        <Loader2 className={cn('animate-spin', SIZES[size].avatar)} />
        Loading…
    </div>
);

const ChipView: FC<UserChipProps & { userId: UserId; profile: AuthProfile | undefined }> = ({
    userId,
    profile,
    size = 'md',
    showEmail,
    layout = 'inline',
    align = 'start',
    link,
    className,
}) => {
    const displayName = profile?.name?.trim() || profile?.email || '';
    const name = displayName || userId;
    const email = showEmail && profile?.email && profile.email !== name ? profile.email : undefined;

    const chip = (
        <div
            className={cn(
                'flex min-w-0 gap-2 font-normal',
                layout === 'stacked' ? 'flex-col' : 'flex-row',
                layout === 'stacked' ? (align === 'end' ? 'items-end text-right' : 'items-start') : 'items-center',
                layout === 'inline' && align === 'end' ? 'justify-end text-right' : undefined,
                link ? undefined : className,
            )}
        >
            {/* An empty name falls back to "?" — a real person's initials over an id nobody has is a lie. */}
            <UserAvatar imageUrl={profile?.imageUrl} name={displayName} className={SIZES[size].avatar} />
            {/* `w-full`, because the column aligns its items rather than stretching them — without
                it these size to their own content and `truncate` has nothing to clamp against. */}
            <div className={cn('flex min-w-0 flex-col', align === 'end' ? 'items-end text-right' : 'items-start')}>
                <span className={cn('w-full truncate text-foreground', SIZES[size].name)}>{name}</span>
                {email ? <span className="w-full truncate text-xs text-muted-foreground">{email}</span> : null}
            </div>
        </div>
    );

    if (!link) return chip;
    return (
        <Link to={`/directory/users/${userId}`} className={cn('block w-fit rounded hover:underline', className)}>
            {chip}
        </Link>
    );
};
